import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Card, Space, Typography, Modal, Descriptions, Divider, Tag, message, Image, Empty } from 'antd';
import { CheckCircleOutlined, CloseOutlined, DeleteOutlined, EditOutlined, } from '@ant-design/icons';
import { checkPermission } from '@/utils/permission';
import { approveGeneralExam, getGeneralExamsByCustomer, getSpecificGeneralExam, removeGeneralExam, unapproveGeneralExam } from '@/redux/actions/generalExamActions';
import { isValidDate, getTeethStatusLabel } from '@/utils/helpers';
import GeneralExamForm from './GeneralExamForm';
import PrintButton from '@/components/PrintButton/PrintButton';
import PrintGeneralExam from './PrintGeneralExam';
import HtmlParser from 'react-html-parser';

const { Text } = Typography;
const { confirm } = Modal;

const GeneralExamList = ({ customer, onCountChange }) => {
    const customerId = customer?.id;
    const dispatch = useDispatch();

    const [isLoading, setIsLoading] = useState(false);
    const [formVisible, setFormVisible] = useState(false);
    const [editingRecord, setEditingRecord] = useState(null);

    const allowUpdate = checkPermission('general_exams', 'update');
    const allowDelete = checkPermission('general_exams', 'remove');
    const allowApproved = checkPermission('general_exams', 'approve');
    const allowUnapproved = checkPermission('general_exams', 'unapprove');

    const generalExamList = useSelector((s) => s.generalExamReducer.generalExamList) || [];

    const loadGeneralExams = async () => {
        try {
            setIsLoading(true);
            await dispatch(getGeneralExamsByCustomer({}, customerId));
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (customerId) {
            loadGeneralExams();
        };
    }, [customerId]);

    const handleOpenEditForm = async (id) => {
        try {
            const res = await dispatch(getSpecificGeneralExam(id));
            setEditingRecord(res);
            setFormVisible(true);
        } catch (error) {
            message.error('Không thể tải dữ liệu phiếu khám tổng quát');
        }
    };

    const handleCancelForm = () => {
        setFormVisible(false);
        setEditingRecord(null);
    };

    const handleDelete = (exam) => {
        confirm({
            title: 'Xác nhận xoá',
            content: 'Bạn có chắc chắn muốn xoá phiếu khám tổng quát này không?',
            okText: 'Xoá',
            okType: 'danger',
            cancelText: 'Huỷ',
            onOk: async () => {
                await dispatch(removeGeneralExam([exam.id]));
                loadGeneralExams();
            },
        });
    };

    const handleApprove = async (exam) => {
        if (!exam?.id) {
            message.error("Chưa có phiếu khám tổng quát để xác nhận");
            return;
        }

        Modal.confirm({
            title: "Xác nhận",
            content: "Bạn có chắc muốn xác nhận phiếu khám tổng quát?",
            okText: "Đồng ý",
            cancelText: "Hủy",
            onOk: async () => {
                setIsLoading(true);
                try {
                    await dispatch(approveGeneralExam(exam.id));
                    await loadGeneralExams(); // refresh lại data
                } finally {
                    setIsLoading(false);
                }
            }
        });
    };

    const handleUnapprove = async (exam) => {
        if (!exam?.id) return;

        Modal.confirm({
            title: "Xác nhận",
            content: "Bạn có chắc muốn hủy xác nhận phiếu khám tổng quát?",
            okText: "Đồng ý",
            cancelText: "Hủy",
            onOk: async () => {
                setIsLoading(true);
                try {
                    await dispatch(unapproveGeneralExam(exam.id));
                    await loadGeneralExams(); // refresh lại data
                } finally {
                    setIsLoading(false);
                }
            }
        });
    };

    useEffect(() => {
        if (onCountChange) {
            onCountChange(generalExamList?.length || 0);
        }
    }, [generalExamList]);

    if (!generalExamList?.length) {
        return (
            <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={<span>Chưa có phiếu khám tổng quát nào.</span>}
            />
        );
    }

    return (
        <div>
            {generalExamList.map((exam, index) => {
                const isApproved = exam.is_approved || false;

                return (
                    <Card
                        key={exam.id || index}
                        style={{
                            marginBottom: 24,
                            border: '1px solid #e0e0e0',
                            borderBottom: '5px solid transparent',
                            borderImage: 'linear-gradient(to right, #1677ff, #52c41a)',
                            borderImageSlice: 1,
                            boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                            borderRadius: 6,
                        }}
                        title={
                            <Space direction="vertical" size={0}>
                                <Text strong>Ngày khám: {isValidDate(exam.created_at, true)}</Text>
                                <Text strong>
                                    <span style={{ color: '#d4380d', fontWeight: 500, fontStyle: 'italic' }}>
                                        {exam.chan_doan_so_bo || 'Chưa có chẩn đoán'}
                                    </span>
                                </Text>
                            </Space>
                        }
                        extra={
                            <Space>
                                <PrintButton printText="In" size="small">
                                    <PrintGeneralExam customer={customer} data={exam} />
                                </PrintButton>
                                <Button
                                    icon={<EditOutlined />}
                                    size="small"
                                    type="primary"
                                    onClick={() => handleOpenEditForm(exam.id)}
                                    disabled={!allowUpdate || isApproved}
                                >
                                    Sửa
                                </Button>
                                <Button
                                    icon={<DeleteOutlined />}
                                    size="small"
                                    danger
                                    onClick={() => handleDelete(exam)}
                                    disabled={!allowDelete || isApproved}
                                >
                                    Xóa
                                </Button>
                            </Space>
                        }
                    >
                        <Divider orientation="left" orientationMargin="0" className="custom-section-title">
                            I. LÝ DO ĐẾN KHÁM
                        </Divider>
                        <Descriptions size="small" column={1}>
                            <Descriptions.Item
                                label="Lý do đến khám"
                                span={2}
                            >
                                {(exam.ly_do_den_kham || []).join(', ') || '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Lý do khác" span={2} >
                                {exam.ly_do_khac || '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Mong muốn của khách hàng" span={2} >
                                {exam.mong_muon_khach_hang || '-'}
                            </Descriptions.Item>
                        </Descriptions>

                        <Divider
                            orientation="left"
                            orientationMargin="0"
                            className="mt-3 custom-section-title"
                        >
                            II. BỆNH LÝ CẦN LƯU Ý
                        </Divider>
                        <Descriptions size="small" column={1}>
                            <Descriptions.Item label="Các bệnh lý đã và đang mắc phải">
                                {exam?.diseases && Object.keys(exam?.diseases).length > 0
                                    ? Object.entries(exam?.diseases)
                                        .filter(([_, value]) => value === 'yes')
                                        .map(([name]) => name)
                                        .join(', ')
                                    : '-'}
                            </Descriptions.Item>
                        </Descriptions>

                        <Divider orientation="left" orientationMargin="0" className="mt-3 custom-section-title">
                            III. KHÁM TỔNG QUÁT RĂNG MIỆNG
                        </Divider>
                        <Descriptions size="small" column={1}>
                            <Descriptions.Item label="Các răng bị ảnh hưởng">
                                {Array.isArray(exam?.teeth) && exam.teeth.length > 0 ? (
                                    <Space wrap>
                                        {exam.teeth
                                            .filter(t => t.status !== "none")
                                            .map(t => {
                                                //console.log('t.note', t.note);
                                                const note = t.note?.trim();
                                                return (
                                                    <span
                                                        key={t.id}
                                                        style={{
                                                            display: "inline-block",
                                                            background: "#e6f7ff",
                                                            color: "#1677ff",
                                                            border: "1px solid #91d5ff",
                                                            padding: "2px 8px",
                                                            borderRadius: 4,
                                                            whiteSpace: "normal",
                                                            wordBreak: "break-word",
                                                            maxWidth: "100%",
                                                        }}
                                                    >
                                                        {getTeethStatusLabel(t.status)} - R{t.tooth_number}
                                                        {note && (
                                                            <>
                                                                {" ("}
                                                                {HtmlParser(note)}
                                                                {")"}
                                                            </>
                                                        )}
                                                    </span>
                                                );
                                            })}
                                    </Space>
                                ) : (
                                    <Text type="secondary">Không có răng bị ảnh hưởng</Text>
                                )}
                            </Descriptions.Item>
                            <Descriptions.Item
                                label="Tình trạng cao răng"
                            >
                                {exam.tinh_trang_cao_rang || '-'}
                            </Descriptions.Item>
                            <Descriptions.Item
                                label="Tình trạng nướu (lợi)"
                            >
                                {exam.tinh_trang_nuou || '-'}
                            </Descriptions.Item>
                            <Descriptions.Item
                                label="Màu sắc răng"
                            >
                                {exam.mau_sac_rang || '-'}
                            </Descriptions.Item>
                            <Descriptions.Item
                                label="Tình trạng răng khôn"
                            >
                                {exam.tinh_trang_rang_khon || '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Tình trạng khớp cắn" span={2}>
                                <Space direction="vertical" size={0}>
                                    <Text>- Đau khớp: {exam.pain === 'yes' ? 'Có' : 'Không'}</Text>
                                    <Text>- Phát tiếng kêu: {exam.sound === 'yes' ? 'Có' : 'Không'}</Text>
                                    <Text>- Đường há zích-zắc: {exam.zigzag === 'yes' ? 'Có' : 'Không'}</Text>
                                </Space>
                            </Descriptions.Item>
                        </Descriptions>

                        <Divider orientation="left" orientationMargin="0" className="mt-3 custom-section-title">
                            IV. CHẨN ĐOÁN TRÊN PHIM X-QUANG
                        </Divider>
                        <Descriptions size="small" column={1}>
                            <Descriptions.Item label="Hình ảnh X-quang">
                                <Image.PreviewGroup>
                                    <Space wrap>
                                        {(Array.isArray(customer?.x_quang_images)
                                            ? customer.x_quang_images
                                            : []
                                        ).length > 0 ? (
                                            customer.x_quang_images.map((img, index) => (
                                                <Image
                                                    key={index}
                                                    width={100}
                                                    height={80}
                                                    src={img?.file_url_thumb || 'https://placehold.co/200x200?text=No+Image'}
                                                    preview={{
                                                        src: img.file_url_original,  // khi phóng to, mở ảnh gốc
                                                    }}
                                                    alt={`X-quang ${index + 1}`}
                                                    fallback="https://placehold.co/200x200?text=No+Image"
                                                    style={{
                                                        width: "100%",   // không vượt quá khung
                                                        height: "100%",  // luôn giữ tỷ lệ
                                                        objectFit: "cover", // luôn hiển thị toàn bộ ảnh
                                                        objectPosition: "center center",
                                                    }}
                                                />
                                            ))
                                        ) : (
                                            <Text type="secondary">Không có hình ảnh X-quang</Text>
                                        )}
                                    </Space>
                                </Image.PreviewGroup>
                            </Descriptions.Item>
                            <Descriptions.Item label="Chẩn đoán trên phim X-quang">
                                {exam.chan_doan_tren_phim_x_quang || '-'}
                            </Descriptions.Item>
                        </Descriptions>

                        <Divider orientation="left" orientationMargin="0" className="mt-3 custom-section-title">
                            V. CHẨN ĐOÁN SƠ BỘ
                        </Divider>
                        <Descriptions size="small" column={1}>
                            <Descriptions.Item label="Chẩn đoán sơ bộ">
                                {exam.chan_doan_so_bo || '-'}
                            </Descriptions.Item>
                        </Descriptions>

                        <Divider orientation="left" orientationMargin="0" className="mt-3 custom-section-title">
                            VI. HƯỚNG ĐIỀU TRỊ
                        </Divider>
                        <Descriptions size="small" column={1}>
                            <Descriptions.Item label="Lấy cao răng, điều trị viêm lợi">
                                {exam.lay_cao_rang || '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Trám các răng">
                                {exam.tram_cac_rang || '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Điều trị tủy các răng">
                                {exam.dieu_tri_tuy_cac_rang || '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Trồng các răng">
                                {exam.trong_cac_rang || '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Làm cầu, chụp">
                                {exam.lam_cau_chup || '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Nhổ răng">
                                {exam.nho_rang || '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Chăm sóc & điều trị khác" span={2}>
                                {Array.isArray(exam.dieu_tri_khac) && exam.dieu_tri_khac.length > 0
                                    ? exam.dieu_tri_khac.join(", ")
                                    : "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="Chỉ định khác" span={2}>
                                {exam.chi_dinh_khac || '-'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Chi tiết chỉ định" span={2}>
                                {exam.chi_tiet_chi_dinh || '-'}
                            </Descriptions.Item>
                        </Descriptions>

                        <Divider orientation="left" orientationMargin="0" className="mt-3 custom-section-title">
                            VII. NỘI DUNG TƯ VẤN
                        </Divider>
                        <Descriptions size="small" column={1}>
                            <Descriptions.Item>
                                {exam?.noi_dung_tu_van || "-"}
                            </Descriptions.Item>
                        </Descriptions>

                        <Divider orientation="left" orientationMargin="0" className="mt-3 custom-section-title">
                            VIII. THÔNG TIN NHÂN SỰ
                        </Divider>
                        <Descriptions size="small" column={1}>
                            <Descriptions.Item label="Người tạo phiếu">
                                {exam.created_user?.user_role?.name || "-"} - {exam.created_user?.full_name || "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="Bác sĩ phụ trách">
                                {exam.doctor?.full_name || "-"}
                            </Descriptions.Item>
                            <Descriptions.Item label="Nhân viên tư vấn">
                                {Array.isArray(exam.nhan_vien_tu_van) && exam.nhan_vien_tu_van.length > 0
                                    ? exam.nhan_vien_tu_van.join(", ")
                                    : "-"}
                            </Descriptions.Item>
                        </Descriptions>

                        <div className='mt-4'>
                            {isApproved ? (
                                <>
                                    <Space align="center" size="middle">
                                        {allowUnapproved && (
                                            <Button
                                                htmlType="button"
                                                icon={<CloseOutlined />}
                                                onClick={() => handleUnapprove(exam)}
                                                danger
                                                size="large"
                                            >
                                                Hủy xác nhận của bác sĩ
                                            </Button>
                                        )}

                                        <Text style={{ color: 'rgba(0, 0, 0, 0.45)', fontStyle: 'italic' }}>
                                            Xác nhận bởi: {exam?.approved_by?.user_role?.name} - {exam?.approved_by?.full_name} - {isValidDate(exam?.approved_at, true)}
                                        </Text>
                                    </Space>
                                </>
                            ) : (
                                allowApproved && (
                                    <Button
                                        type="primary"
                                        style={{ background: "#52c41a", borderColor: "#52c41a" }}
                                        icon={<CheckCircleOutlined />}
                                        onClick={() => handleApprove(exam)}
                                        size="large"
                                        disabled={false}
                                    >
                                        Xác nhận của bác sĩ
                                    </Button>
                                )
                            )}
                        </div>
                    </Card>
                );
            })}

            <GeneralExamForm
                customer={customer}
                visible={formVisible}
                record={editingRecord}
                onCancel={handleCancelForm}
            />
        </div>
    );
};

export default GeneralExamList;
